import SoundManager from "../../../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import HubEvent from "../../../../../app/hub/event/HubEvent";
import MediatorManager from "../../../../../core/application/MediatorManager";
import EmailInteractionEvent from "../../../../email/controller/EmailInteractionEvent";
import EmailBoxType from "../../../../email/model/EmailBoxType";
import EmailInterfaceEvent from "../../../../email/model/EmailInterfaceEvent";
import LaptopNavigateEvent from "../../../model/LaptopNavigateEvent";
import LaptopSkinKeyRegistry from "../../../model/LaptopSkinKeyRegistry";
import LaptopSoundType from "../../../sound/LaptopSoundType";
import AbstractLaptopMediator from "../../AbstractLaptopMediator";

export default class LaptopEmailScreenMediator extends AbstractLaptopMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {RevisableLanguagePack} languagePack 
     * @param {MediatorManager} mediatorManager 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, languagePack, mediatorManager, soundManager) {
        super(target, dispatcher);

        this._languagePack = languagePack;
        this._mediatorManager = mediatorManager;
        this._soundManager = soundManager;
    }

    initialize() {
        super.initialize();

        this._lastOpenedInbox = undefined;

        this._target.applicationTitleTextField.text = this._languagePack.getText("laptop.emailScreen.title");

        this._backButton = this.createButton(this._target.backButton, this._handleBackButton.bind(this), null, LaptopSoundType.CLICK);
        this._closeButton = this.createButton(this._target.closeButton, this._handleCloseButton.bind(this), null, LaptopSoundType.CLICK);

        this._emailBoxSelectionMediator = this._mediatorManager.createMediator(this._target.boxButtons, LaptopSkinKeyRegistry.EMAIL_BOX_SELECTION_SKIN);

        this._target.conversationList.customData = {
            boxType: EmailBoxType.INBOX,
            skinKeyRegistry: LaptopSkinKeyRegistry,
            smallNpcImages: false
        };
        this._emailConversationListMediator = this._mediatorManager.createMediator(this._target.conversationList, LaptopSkinKeyRegistry.EMAIL_CONVERSATION_LIST_SKIN);

        this._target.conversation.customData = {
            skinKeyRegistry: LaptopSkinKeyRegistry
        };
        this._emailConversationMediator = this._mediatorManager.createMediator(this._target.conversation, LaptopSkinKeyRegistry.EMAIL_CONVERSATION_SKIN);
        this._target.conversation.visible = false;

        this._hideKeyDecisionLogo();

        // prevent click through
        this._target.addEventListener('click', () => { });

        this.registerEventListener(EmailInteractionEvent.OPEN_MAILBOX, this._handleOpenMailboxRequest.bind(this));
        this.registerEventListener(EmailInteractionEvent.OPEN_CONVERSATION, this._handleOpenConversationRequest.bind(this));

        this.registerEventListener(EmailInteractionEvent.OPEN_DRAFT_CHOICE_PICKER, this._handleOpenDraftChoicePickerRequest.bind(this));
        this.registerEventListener(EmailInteractionEvent.CLOSE_DRAFT_CHOICE_PICKER, this._handleCloseDraftChoicePickerRequest.bind(this));
        this.registerEventListener(EmailInteractionEvent.SUBMIT_DRAFT, this._handleSubmitDraft.bind(this));

        this.registerEventListener(EmailInterfaceEvent.LOCK_INTERFACE, this._handleLockInterface.bind(this));
        this.registerEventListener(EmailInterfaceEvent.UNLOCK_INTERFACE, this._handleUnlockInterface.bind(this));

        this._updateBackButton();
    }

    destroy() {
        super.destroy();

        this._emailBoxSelectionMediator.destroy();

        this._emailConversationListMediator.destroy();
        this._emailConversationMediator.destroy();
    }

    _hideKeyDecisionLogo() {
        this._target.NewKeyDecisionFound.alpha = 0;
    }

    _showKeyDecisionLogo() {
        this.dispatch(LaptopNavigateEvent.HIDE_OVERLAY);
        this._target.NewKeyDecisionFound.alpha = 1;
        this._target.NewKeyDecisionFound.timeline.loop = 0;
        this._target.NewKeyDecisionFound.timeline.gotoAndPlay(0);
        this._target.NewKeyDecisionFound.timeline.on('change', (evt) => {
            if (this._target.NewKeyDecisionFound.timeline.paused) {
                evt.remove();
                this.dispatch(HubEvent.REFRESH_KEY_DECISIONS_LIST);
            }
        });
    }

    _handleBackButton() {
        this._hideKeyDecisionLogo();

        if (this._emailConversationMediator.getConversation() == undefined || this._lastOpenedInbox == undefined) {
            this.gotoScreen(LaptopSkinKeyRegistry.LAPTOP_MAIN_SCREEN_SKIN);
        } else {
            this._emailConversationListMediator.setBoxType(this._lastOpenedInbox);
            this._lastOpenedInbox = undefined;

            this._target.conversation.visible = false;
            this._target.conversationList.visible = true;
        }

        this._updateBackButton();
    }

    _handleCloseButton() {
        this._hideKeyDecisionLogo();

        if (LaptopEmailScreenMediator.CLOSE_BUTTON_CLOSES_THE_EMAIL_APP) {
            this.gotoScreen(LaptopSkinKeyRegistry.LAPTOP_MAIN_SCREEN_SKIN);
            return;
        }

        if (this._emailConversationMediator.getConversation() == undefined || this._lastOpenedInbox == undefined) {
            this.gotoScreen(LaptopSkinKeyRegistry.LAPTOP_MAIN_SCREEN_SKIN);
        } else {
            this._emailConversationListMediator.setBoxType(this._lastOpenedInbox);
            this._lastOpenedInbox = undefined;

            this._target.conversation.visible = false;
            this._target.conversationList.visible = true;
        }
    }

    _handleOpenMailboxRequest(evt) {
        this._hideKeyDecisionLogo();
        const boxType = evt.params.boxType;
        this._lastOpenedInbox = this._emailConversationMediator.getConversation() == undefined ? boxType : undefined;

        this._emailConversationListMediator.setBoxType(boxType);

        this._target.conversation.visible = false;
        this._target.conversationList.visible = true;

        this._updateBackButton();
    }

    _handleOpenConversationRequest(evt) {
        const conversation = evt.params.conversation;
        if (conversation.isUnlockingKeyDecision && conversation.hasUnreadItems()) {
            this._showKeyDecisionLogo();
        }
        this._emailConversationMediator.setConversation(conversation);
        this._lastOpenedInbox = this._emailConversationListMediator.getBoxType();

        this._target.conversation.visible = true;
        this._target.conversationList.visible = false;

        this._updateBackButton();
    }

    _handleOpenDraftChoicePickerRequest(evt) {
        const mcBlock = evt.params.mcBlock;
        this._draftChoicePickerScreen = this.openScreen(LaptopSkinKeyRegistry.LAPTOP_EMAIL_DRAFT_OPTION_OVERLAY_SKIN, null, {
            mcBlock,
            skinKeyRegistry: LaptopSkinKeyRegistry
        });
    }

    _handleCloseDraftChoicePickerRequest(evt) {
        if (this._draftChoicePickerScreen) {
            this.closeScreen(this._draftChoicePickerScreen);
            this._draftChoicePickerScreen = null;
        }
    }

    _handleSubmitDraft(evt) {
        this.dispatch(EmailInteractionEvent.OPEN_MAILBOX, { boxType: EmailBoxType.INBOX });

        this._target.conversation.visible = false;
        this._target.conversationList.visible = true;
    }

    _handleLockInterface() {
        this._updateBackButton();
        this._closeButton.setEnabled(false);
    }

    _handleUnlockInterface() {
        this._updateBackButton();
        this._closeButton.setEnabled(true);
    }

    _updateBackButton() {
        this._backButton.setEnabled(this._target.conversation.visible);
    }
}

LaptopEmailScreenMediator.CLOSE_BUTTON_CLOSES_THE_EMAIL_APP = true;